/*
 * Every SPA host and the Laravel host its API lives on.
 *
 * Order is not significant: every app host carries the `app.` label and no
 * `app.`-labelled host is itself an SPA host, so no rule's output can be picked
 * up and rewritten again by a later rule. What keeps the table honest is the
 * anchoring in hostPattern below, not the order of these rows.
 */
const SPA_HOSTS = [
    { spa: "coaching-app.test", app: "app.coaching-app.test" },
    { spa: "strawberry.me", app: "app.strawberry.me" },
    { spa: "stagingstrawberry.me", app: "app.stagingstrawberry.me" },
    { spa: "yaz.stagingstrawberry.me", app: "app.stagingstrawberry.me" },
    { spa: "testingstrawberry.me", app: "app.testingstrawberry.me" },
    ...thisCheckoutsHosts(),
];

function thisCheckoutsHosts() {
    const appHost = /^https?:\/\/(app\.[a-z0-9.-]+)\/?$/.exec(
        import.meta.env.VITE_APP_URL ?? "",
    )?.[1];

    return appHost ? [{ spa: appHost.slice("app.".length), app: appHost }] : [];
}

/*
 * Anchored to the start of the string and terminated by a lookahead, so only the
 * URL's own host can match. Four things depend on that: an unanchored needle
 * rewrites "stagingstrawberry.me" via the "strawberry.me" rule (and
 * "testingstrawberry.me" via both); it rewrites a host nested in a query
 * (?next=https://strawberry.me/x); an unterminated match rewrites a lookalike
 * like coaching-app.test.evil.com; and the "stagingstrawberry.me" needle would
 * swallow "yaz.stagingstrawberry.me".
 *
 * A relative href has no scheme, so it falls through untouched — which is
 * correct, since it is already same-host.
 */
function hostPattern(host: string) {
    return new RegExp(`^(https?://)${host.replace(/\./g, "\\.")}(?=[/?#]|$)`);
}

export function toAppUrl(url: string) {
    return SPA_HOSTS.reduce(
        (rewritten, { spa, app }) =>
            rewritten.replace(hostPattern(spa), `$1${app}`),
        url,
    );
}

/*
 * The reverse mapping is ambiguous — stagingstrawberry.me and
 * yaz.stagingstrawberry.me both use app.stagingstrawberry.me — so it is
 * resolved against the host this bundle is actually being served from rather
 * than by walking the table. That is also what lets one build serve two hosts:
 * the yaz preview ships the staging bundle verbatim and still links to itself.
 *
 * The host parameter exists so tests don't have to stub window.location.
 */
export function toSpaUrl(url: string, host: string = window.location.host) {
    const pair = SPA_HOSTS.find((candidate) => candidate.spa === host);

    if (!pair) {
        return url;
    }

    return url.replace(hostPattern(pair.app), `$1${pair.spa}`);
}

export function appHost(host: string = window.location.host) {
    const pair = SPA_HOSTS.find((candidate) => candidate.spa === host);

    return pair?.app ?? host;
}

export function isAppHostRequest(url: string | undefined) {
    if (!url) {
        return false;
    }

    try {
        return new URL(url, window.location.origin).host === appHost();
    } catch {
        return false;
    }
}
